import { useState } from "react";

import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

import numbers from "core/numbers";

import Form from "ui/atom/Form";
import Button from "ui/base/Button";
import Input from "ui/base/Input";
import Modal from "ui/Modal";
import Page from "ui/Page";

import { useStart } from "features/start/fabric";

import { useUser } from "areas/account/fabric";
import Section from "areas/account/ui/Section";
import Tile from "areas/account/ui/Tile";


Profile.Verified = () => <Chip label="Verified" size="small" color="success" />;

const ActionModal = ({ open, close, value, title, submit, type, loading, ...props }) => {
	const [v, setV] = useState(value || "");

	const isPhone = type === "tel";

	const handleSubmit = async () => {
		submit && await submit(v);
		close();
	};

	const change = (e) => setV(e.target.value);

	return (
		<Modal open={open} onClose={close} disableRestoreFocus keepMounted={false}>
			<Modal.Title onClose={close}>
				{title}
			</Modal.Title>
			<Modal.Content>
				<Form id={title} onSubmit={handleSubmit} >
					{isPhone ? <Input.Phone value={v} onChange={change} {...props} /> : <Input value={v} onChange={change} {...props} autoFocus />}
				</Form>
			</Modal.Content>
			<Modal.Actions>
				<Button variant="contained" color="error" onClick={close} disabled={loading}>Cancel</Button>
				<Button type="submit" form={title} variant="contained" color="success" loading={loading}>Save</Button>
			</Modal.Actions>
		</Modal>
	);
};

Profile.Action = ({ verified, ...props }) => {
	const user = useUser();
	const [open, setOpen] = useState(false);

	const toggle = () => setOpen(s => !s);
	const close = () => setOpen(false);

	if (verified) return <Profile.Verified />;
	return (
		<>
			<Button.Icon icon="square-pen" size={18} onClick={toggle} />
			{user.data && <ActionModal open={open} close={close} loading={user.mutation.isPending} {...props} />}
		</>
	);
};

export default function Profile() {
	const theme = useTheme();
	const user = useUser();
	const start = useStart();
	const settings = start.settings.user;

	const style = {
		body: {
			mt: theme.spacing(2.5),
			gap: theme.spacing(3)
		}
	};

	return (
		<Page maxWidth="md">
			<Page.Header title="Profile" subtitle="Manage your Profile" />
			<Divider flexItem />
			<Page.Body sx={style.body}>
				<Section title="Your profile information">
					<Tile.Icon
						icon="file"
						title="Name"
						description={user.description.name}
						errors={user.error?.errors?.name}
						loading={user.isLoading}
						action={
							<Profile.Action
								value={user.data?.name}
								title="What is your name?"
								submit={(v) => user.mutation.mutate({ name: v })}
								maxLength={settings.name.max}
								inputProps={{ minLength: settings.name.min }}
								required
							/>}
					/>
					<Tile.Icon
						icon="user"
						title="Nickname"
						description={user.description.nickname}
						errors={user.error?.errors?.nickname}
						loading={user.isLoading}
						action={
							<Profile.Action
								value={user.data?.nickname}
								title="How your name appears on your profile"
								submit={(v) => user.mutation.mutate({ nickname: v })}
								maxLength={settings.name.max}
							/>}
					/>
					<Tile.Icon
						icon="mail"
						title="Email"
						description={user.description.email}
						loading={user.isLoading}
						errors={user.authMutation.error?.errors?.email}
						action={
							<Profile.Action
								value={user.data?.email?.value}
								type="email"
								title="What is your email address?"
								verified={user.verified.email}
								submit={user.updateEmail}
								maxLength={settings.email.max}
								inputProps={{ minLength: settings.email.min }}
								errors={user.authMutation.error?.errors?.email}
								required
							/>}
					/>
					<Tile.Icon
						icon="phone"
						title="Phone"
						description={user.description.phone}
						errors={user.authMutation.error?.errors?.phone}
						loading={user.isLoading}
						action={<Profile.Action
							value={numbers.toPhone(user.phone)}
							country="+1"
							type="tel"
							verified={user.verified.phone}
							title="What is your mobile phone number?"
							submit={user.updatePhone}
							errors={user.authMutation.error?.errors?.phone}
							required
						/>}
					/>
				</Section>
			</Page.Body>
		</Page>
	);
}